import { useEffect, useRef, useState } from 'react';
import { getOwnRelease } from './ownRelease';
import { clearAllClientStorage, decideVersionAction, triggerPageReload } from './versionActions';

// =========================================================
// Version polling hook
// =========================================================
//
// Detects when a new version of the UI has been deployed while the
// user's browser session is still running the old one. The hook is
// wired up once near the root of the tree (see `EslManager.tsx`);
// the returned `updateAvailable` flag is consumed by the update
// banner component.
//
// This is the backoffice counterpart to the worker UI's
// `src/helper/useVersionPoll.ts`; the two share an endpoint contract
// and the same decision rule.
//
// Design decisions worth noting for future readers:
//
//   - The hook holds no record of what it has seen. It compares the
//     served release against the release baked into this bundle
//     (`getOwnRelease`), so a forced reload that lands back on a stale
//     replica is retried on the next poll instead of being written off
//     as already done. A rolling deploy can reload a tab more than
//     once; it stops when the tab actually runs what the cluster
//     serves.
//
//   - Two outcomes on a version change. By default a change only
//     raises the banner and the user taps to reload. When the
//     deployment sets `forceUiReload`, the hook wipes client storage
//     and reloads on its own; the decision lives in
//     `decideVersionAction`.
//
//   - Keep polling after the banner. The banner is not a terminal
//     state: an operator can flip `forceUiReload` on a later deploy to
//     escalate an already-shown banner into a forced reload, so the
//     poll must stay alive to see it.
//
//   - False-positive safety. A failed fetch never flips the flag;
//     only a successful response with a *different* non-empty
//     release does. "I can't reach the server" and "a new version
//     is live" look the same at the network layer and we must not
//     conflate them.
//
//   - Cache-busting is belt-and-suspenders. `cache: 'no-store'` +
//     server-side no-store headers are already enough in theory;
//     appending `?t=${Date.now()}` makes caching physically
//     impossible under any intermediary that keys on URL.
//
//   - Multiple triggers. 30s timer for the steady state, window
//     `online` for reconnects, document `visibilitychange` for tab
//     re-focus (timers get throttled in the background), and
//     `pageshow` with `event.persisted` for browser bfcache restore
//     where in-memory React state is reinstated wholesale.
//
//   - Force fires once per page. An in-memory latch stops overlapping
//     probes (timer + focus + online) from both reaching the reload.
//     It is re-checked after every await, because a probe suspended in
//     `fetch` can resume after another has already latched.

const DEFAULT_INTERVAL_MS = 30_000;
const FETCH_TIMEOUT_MS = 5_000;

interface VersionResponse {
  release?: unknown;
  forceUiReload?: unknown;
}

export interface VersionPollResult {
  updateAvailable: boolean;
}

export default function useVersionPoll(endpoint: string, intervalMs: number = DEFAULT_INTERVAL_MS): VersionPollResult {
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const isForcingRef = useRef<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const probe = async (): Promise<void> => {
      // Once a forced reload is in flight the page is on its way out;
      // further probes would only race the navigation.
      if (cancelled || isForcingRef.current) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT_MS);

      try {
        const separator = endpoint.includes('?') ? '&' : '?';
        const bustedUrl = `${endpoint}${separator}t=${Date.now()}`;

        const response = await fetch(bustedUrl, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as VersionResponse;
        // A probe that suspended in `fetch` or `json` can resume after
        // another probe has already started the reload.
        if (cancelled || isForcingRef.current) {
          return;
        }

        const release = typeof data.release === 'string' ? data.release : '';
        if (release === '') {
          return;
        }

        const action = decideVersionAction({ release, forceUiReload: data.forceUiReload === true }, getOwnRelease());

        switch (action.kind) {
          case 'banner':
            setUpdateAvailable(true);
            break;
          case 'force':
            // Latch first: everything after this point is synchronous,
            // so no other probe can enter the branch behind us.
            isForcingRef.current = true;
            clearAllClientStorage();
            triggerPageReload();
            break;
          case 'none':
          default:
            // Includes the case where a poll matches again after a
            // banner was shown, which un-shows it: the release that
            // prompted the banner is no longer the one being served.
            setUpdateAvailable(false);
            break;
        }
      } catch {
        // Transient failure — not evidence of a deploy. Retry next tick.
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    void probe();

    const timerId = window.setInterval(() => {
      void probe();
    }, intervalMs);

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void probe();
      }
    };
    const handleOnline = (): void => {
      void probe();
    };
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        void probe();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [endpoint, intervalMs]);

  return { updateAvailable };
}
